import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { FIREBASE_APP } from "../Firebase";
import { getRemoteEnv } from '../config/remoteEnv';
import { httpsCallableWithAppId } from './CallableWrapper';


class TimeService {

    private static timeToMinutesSinceMidnight(timeStr: string) {

        const [hours, minutes] = timeStr.split(':').map(Number);
              return hours * 60 + minutes;
    }

    // ── Schedule description (fulfilment-mode indicator) ──────────────────
    // Turns a per-mode timings object into "is it open now" plus a short
    // human label: "Open 24 hours" / "Until 2 AM" / "Closed · opens 5 PM" /
    // "Closed · opens tomorrow 11 AM". Pure — takes the already-fetched
    // time object so callers pay for ONE server-time fetch per sweep.

    private static readonly DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    private static formatMinutes(mins: number): string {
        const m = ((mins % 1440) + 1440) % 1440;
        const h24 = Math.floor(m / 60);
        const mm = m % 60;
        const ampm = h24 < 12 ? 'AM' : 'PM';
        const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
        return mm === 0 ? `${h12} ${ampm}` : `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
    }

    /** Normalized open ranges [startMin, endMin) for one day, INCLUDING the
     *  early-morning carry-over of the PREVIOUS day's overnight blocks. */
    private static dayRanges(timings: any, dayIndex: number): Array<[number, number]> {
        const ranges: Array<[number, number]> = [];
        const collect = (dIdx: number, carryOnly: boolean) => {
            const blocks = timings?.[TimeService.DAY_NAMES[dIdx]];
            const list = !blocks ? [] : Array.isArray(blocks) ? blocks : [blocks];
            for (const b of list) {
                if (!b) continue;
                if (b.allDay) { if (!carryOnly) ranges.push([0, 1440]); continue; }
                if (!b.start || !b.end) continue;
                const s = TimeService.timeToMinutesSinceMidnight(b.start);
                const e = TimeService.timeToMinutesSinceMidnight(b.end);
                if (carryOnly) { if (e <= s && e > 0) ranges.push([0, e]); }
                else ranges.push(e <= s ? [s, 1440] : [s, e]);
            }
        };
        collect(dayIndex, false);
        collect((dayIndex + 6) % 7, true); // yesterday's overnight tail
        return ranges.sort((a, b) => a[0] - b[0]);
    }

    public static describeSchedule(
        timings: any,
        timeObj: { day: string; minutesSinceMidnight: number }
    ): { open: boolean; label: string } {
        // No schedule = no restriction.
        if (!timings || Object.keys(timings).length === 0) return { open: true, label: 'Open 24 hours' };
        const dayIndex = TimeService.DAY_NAMES.indexOf(timeObj.day.toLowerCase());
        if (dayIndex < 0) return { open: true, label: '' };
        const now = timeObj.minutesSinceMidnight;
        const today = TimeService.dayRanges(timings, dayIndex);

        const covering = today.find(([s, e]) => now >= s && now < e);
        if (covering) {
            // Follow the span across midnight (11:00–02:00 → "Until 2 AM").
            let end = covering[1];
            let merged = today.find(([s, e]) => s <= end && e > end);
            while (merged) { end = merged[1]; merged = today.find(([s, e]) => s <= end && e > end); }
            if (end >= 1440) {
                const tomorrow = TimeService.dayRanges(timings, (dayIndex + 1) % 7);
                const cont = tomorrow.find(([s]) => s === 0);
                if (cont && cont[1] >= 1440) return { open: true, label: 'Open 24 hours' };
                if (cont) return { open: true, label: `Until ${TimeService.formatMinutes(cont[1])}` };
                if (covering[0] === 0 && today.length === 1) return { open: true, label: 'Open 24 hours' };
                return { open: true, label: 'Until midnight' };
            }
            return { open: true, label: `Until ${TimeService.formatMinutes(end)}` };
        }

        // Closed — find the next opening within a week.
        const later = today.find(([s]) => s > now);
        if (later) return { open: false, label: `Closed · opens ${TimeService.formatMinutes(later[0])}` };
        for (let ahead = 1; ahead <= 7; ahead++) {
            const d = (dayIndex + ahead) % 7;
            const first = TimeService.dayRanges(timings, d)[0];
            if (first) {
                const when = ahead === 1 ? 'tomorrow ' : `${TimeService.DAY_NAMES[d][0].toUpperCase()}${TimeService.DAY_NAMES[d].slice(1, 3)} `;
                return { open: false, label: `Closed · opens ${when}${TimeService.formatMinutes(first[0])}` };
            }
        }
        return { open: false, label: 'Closed' };
    }

    public static async getCurrentTimeDetails(): Promise<{ day: string; minutesSinceMidnight: number; currentTime: string } | null> {
        const localTime = sessionStorage.getItem('localTime');
        if (localTime) {
            console.log("123: LOCAL TIME PRESENT - NOT CALLING SERVER")
            console.log("123: Local time: ", localTime)
          const timeDiff = parseFloat(sessionStorage.getItem('timeDiff') || '0');
          const currentTime = new Date(localTime);
          const adjustedTime = new Date(currentTime.getTime() + timeDiff * 1000 + (new Date().getTime() - new Date(localTime).getTime()));
            console.log("123: Adjusted time: ", adjustedTime)
          // Get day of the week
          const options: Intl.DateTimeFormatOptions = { weekday: 'long' };
          const day = adjustedTime.toLocaleDateString('en-US', options); // Day of the week
      
          // Get number of minutes since midnight
          const minutesSinceMidnight = adjustedTime.getHours() * 60 + adjustedTime.getMinutes();
      
          // Return the results
          console.log("local time successfully processed")
          return {
            day,
            minutesSinceMidnight,
            currentTime: adjustedTime.toLocaleTimeString() // Current time in HH:MM:SS format
          };
        } else {
          try {
            const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region)
            const getLocalTimeObject = httpsCallableWithAppId(functions, 'getLocalTimeObject');

            console.log("Weird stuff: ", "2024-10-28T01:23:22Z", new Date("2024-10-28T01:23:22Z"))
        
            const result: any = await getLocalTimeObject();

                        console.log("123: GETTING LOCAL TIME FROM SERVER", result)

            console.log("LOCAL TIME OBJ RESPONSE: ", result.data)
            // const merchantTime: string = result.data.split("Z")[0];
            const merchantTime: string = result.data.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
            const deviceTime = new Date().toISOString();
            const merchantTimeDate = new Date(merchantTime);
            const deviceTimeDate = new Date(deviceTime);
            const timeDiff = (merchantTimeDate.getTime() - deviceTimeDate.getTime()) / 1000; 
            console.log("123: setting merchant time - ", merchantTime)
            sessionStorage.setItem('localTime', merchantTime);
            sessionStorage.setItem('timeDiff', timeDiff.toString());
      
            const currentTime = new Date(merchantTime);
            const adjustedTime = new Date(currentTime.getTime() + timeDiff * 1000 + (new Date().getTime() - new Date(merchantTime).getTime()));
  

            



            // Get day of the week
            const options: Intl.DateTimeFormatOptions = { weekday: 'long' };
            const day = adjustedTime.toLocaleDateString('en-US', options); // Day of the week
      
            // Get number of minutes since midnight
            const minutesSinceMidnight = adjustedTime.getHours() * 60 + adjustedTime.getMinutes();
      
            // Return the results
            console.log("local time successfully processed from server")
            return {
              day,
              minutesSinceMidnight,
              currentTime: adjustedTime.toLocaleTimeString() // Current time in HH:MM:SS format
            };
          } catch (error) {
            console.error("Error fetching current time:", error);
            throw error;
          }
        }
      }


    public static checkActiveMenu = async (menuSettings: any, menuName: string, timeObj?: any ) => {
        console.log("check active menu call")
        try{
        
            let timeObject: any;

            if (timeObj) {
                timeObject = timeObj
            }
            else {
                timeObject = await TimeService.getCurrentTimeDetails();
            }

            if (timeObject) {
                let day = timeObject.day.toLowerCase();
                let minutesSinceMidnight = timeObject.minutesSinceMidnight;

                let menuData = menuSettings.filter((item: any) => item.menuName === menuName)[0];
                
                if (!menuData || !menuData.timings || !menuData.timings[day]) {
                    return false;
                }

                // Use the new isTimeInRange method that handles both formats
                return this.isTimeInRange(menuData.timings[day], minutesSinceMidnight);
            }
            else {
                console.log("ITEM ACCEPTED BUT ERROR GETTING TIME")
                return true;
            }
        }
        catch (error){
            console.log("ERROR BUT RETURNING: ", error)
            return false;
        }
    }

    public static checkAnyMenuActive = async (menuSettings: any, menuNames: string[]) => {

        try{

            let timeObject = await TimeService.getCurrentTimeDetails();
            console.log("TimeObj2: ", timeObject)

            for (let menuName of menuNames) {
                if (await this.checkActiveMenu(menuSettings, menuName, timeObject)) {
                    return true;
                }
            }

            return false;

        }
        catch {
            return false;
        }
    
    }

    public static getAllowedMenus = async (menuSettings: any) => {

        try {

            let timeObject = await TimeService.getCurrentTimeDetails();
            console.log("TimeObj3: ", timeObject)
            let outputMenus: string[] = []

            for (let menu of menuSettings) {
                if (menu.enabled && !menu.deleted && await this.checkActiveMenu(menuSettings, menu.menuName, timeObject)) {
                    outputMenus.push(menu.menuName)
                }
            }

            return outputMenus;
        }
        catch {
            console.log("Error so allowing all.")
            return menuSettings.map((menu: any) => menu.menuName)
        }
    }

/**
 * Preprocesses opening hours to handle overnight shifts by adding the next-day portion.
 * For example, Saturday 21:00-05:00 becomes:
 * - Saturday: 21:00-05:00 (kept as is - will match times >= 21:00)
 * - Sunday: 00:00-05:00 (added - will match times < 05:00)
 */
private static preprocessOvernightHours(openingHours: any): any {
    console.log('🔄 Preprocessing overnight hours...');
    console.log('Original hours:', JSON.stringify(openingHours, null, 2));
    
    const DAYS_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const processedHours: any = {};
    
    // Initialize all days with empty arrays
    DAYS_ORDER.forEach(day => {
        processedHours[day] = [];
    });
    
    // Process each day
    DAYS_ORDER.forEach((day, dayIndex) => {
        const dayTimings = openingHours[day];
        
        if (!dayTimings) {
            console.log(`  ${day}: No timings defined`);
            return;
        }
        
        // Normalize to array format
        let timeBlocks: Array<any> = [];
        if (Array.isArray(dayTimings)) {
            timeBlocks = dayTimings;
        } else if (typeof dayTimings === 'object' && dayTimings.allDay) {
            timeBlocks = [dayTimings];
        } else if (typeof dayTimings === 'object' && (dayTimings.start || dayTimings.end)) {
            timeBlocks = [dayTimings];
        }
        
        console.log(`  ${day}: Processing ${timeBlocks.length} block(s)`);
        
        // Process each time block
        timeBlocks.forEach((block, blockIndex) => {
            // Handle all-day blocks
            if (block.allDay) {
                console.log(`    Block ${blockIndex + 1}: All day - keeping as is`);
                processedHours[day].push({ allDay: true });
                return;
            }
            
            if (!block.start || !block.end) {
                console.log(`    Block ${blockIndex + 1}: Missing start/end - skipping`);
                return;
            }
            
            const startMinutes = this.timeToMinutesSinceMidnight(block.start as string);
            const endMinutes = this.timeToMinutesSinceMidnight(block.end as string);
            
            console.log(`    Block ${blockIndex + 1}: ${block.start} to ${block.end} (${startMinutes} to ${endMinutes} mins)`);
            
            // Always add the original block to current day
            processedHours[day].push({
                start: block.start,
                end: block.end
            });
            
            // Check if this is an overnight shift
            if (endMinutes < startMinutes) {
                console.log(`      🌙 Overnight detected! Adding early morning portion to next day...`);
                
                // Add next day portion (midnight to end)
                const nextDayIndex = (dayIndex + 1) % 7;
                const nextDay = DAYS_ORDER[nextDayIndex];
                
                processedHours[nextDay].push({
                    start: "00:00",
                    end: block.end
                });
                console.log(`      ✓ Added to ${nextDay}: 00:00 to ${block.end}`);
            } else {
                console.log(`      ☀️ Same-day hours - no next-day portion needed`);
            }
        });
    });
    
    // Clean up: convert empty arrays back to [] for consistency
    DAYS_ORDER.forEach(day => {
        if (processedHours[day].length === 0) {
            processedHours[day] = [];
        }
    });
    
    console.log('✅ Preprocessed hours:', JSON.stringify(processedHours, null, 2));
    return processedHours;
}

/**
 * Check if current time falls within any of the time blocks for a given day
 * Supports:
 * - Old format: single object { start, end }
 * - New format: array of objects [{ start, end }, ...]
 * - All-day format: { allDay: true } or [{ allDay: true }]
 * - Overnight format: handled via preprocessing
 */
private static isTimeInRange(dayTimings: any, minutesSinceMidnight: number): boolean {
    console.log('=== isTimeInRange called ===');
    console.log('dayTimings:', JSON.stringify(dayTimings, null, 2));
    console.log('minutesSinceMidnight:', minutesSinceMidnight);
    
    // Normalize to array format
    let timeBlocks: Array<{ start?: string | number; end?: string | number; allDay?: boolean }> = [];
    
    if (!dayTimings) {
        console.log('❌ No dayTimings provided, returning false');
        return false;
    } else if (Array.isArray(dayTimings)) {
        timeBlocks = dayTimings;
        console.log('✓ dayTimings is array, timeBlocks:', timeBlocks);
    } else if (typeof dayTimings === 'object' && dayTimings.allDay) {
        timeBlocks = [dayTimings];
        console.log('✓ dayTimings is single all-day object');
    } else if (typeof dayTimings === 'object' && (dayTimings.start || dayTimings.end)) {
        timeBlocks = [dayTimings];
        console.log('✓ dayTimings is single time block object, converted to array:', timeBlocks);
    } else {
        console.log('❌ dayTimings format not recognized, returning false');
        return false;
    }

    console.log(`Processing ${timeBlocks.length} time block(s)`);

    // Check if current time falls within any time block
    for (let i = 0; i < timeBlocks.length; i++) {
        const block = timeBlocks[i];
        console.log(`\n--- Checking block ${i + 1}/${timeBlocks.length} ---`);
        console.log('Block data:', JSON.stringify(block, null, 2));
        
        // Check for all-day block
        if (block.allDay) {
            console.log('✅ Block is ALL DAY - Store is OPEN');
            return true;
        }

        // Skip blocks without start/end times
        if (!block.start || !block.end) {
            console.log('⚠️ Block missing start or end, skipping');
            continue;
        }

        let blockStart: number;
        let blockEnd: number;

        if (typeof block.start === "string") {
            blockStart = this.timeToMinutesSinceMidnight(block.start);
            console.log(`Start time "${block.start}" converted to ${blockStart} minutes`);
        } else {
            blockStart = block.start;
            console.log(`Start already in minutes: ${blockStart}`);
        }

        if (typeof block.end === "string") {
            blockEnd = this.timeToMinutesSinceMidnight(block.end);
            console.log(`End time "${block.end}" converted to ${blockEnd} minutes`);
        } else {
            blockEnd = block.end;
            console.log(`End already in minutes: ${blockEnd}`);
        }

        console.log(`Block range: ${blockStart} to ${blockEnd} minutes`);
        console.log(`Current time: ${minutesSinceMidnight} minutes`);

        // Handle overnight hours (when end time is before start time)
        if (blockEnd < blockStart) {
            console.log('🌙 OVERNIGHT HOURS DETECTED (end < start)');
            console.log(`Checking if ${minutesSinceMidnight} >= ${blockStart} (late night check)`);
            
            // For overnight blocks, only check if we're in the late night portion (>= start)
            // The early morning portion (< end) is handled by the next day's 00:00-end block
            if (minutesSinceMidnight >= blockStart) {
                console.log('✅ Current time IS within overnight hours (late night) - Store is OPEN');
                return true;
            } else {
                console.log('❌ Current time NOT within overnight hours (late night)');
            }
        } else {
            // Normal hours: within the same day
            console.log('☀️ NORMAL HOURS (same day)');
            console.log(`Checking if ${minutesSinceMidnight} >= ${blockStart} AND ${minutesSinceMidnight} < ${blockEnd}`);
            
            if (minutesSinceMidnight >= blockStart && minutesSinceMidnight < blockEnd) {
                console.log('✅ Current time IS within normal hours - Store is OPEN');
                return true;
            } else {
                console.log('❌ Current time NOT within normal hours');
            }
        }
    }

    console.log('\n❌ No matching time blocks found - Store is CLOSED');
    return false;
}

public static checkStoreIsOpen = async (openingHoursArray: any[], timeObj?: any) => {
    console.log("123: ABC")
    console.log('\n╔══════════════════════════════════════════════════════════╗');
    console.log('║          CHECK STORE IS OPEN - START                    ║');
    console.log('╚══════════════════════════════════════════════════════════╝');
    console.log('openingHoursArray:', JSON.stringify(openingHoursArray, null, 2));
    console.log('123: timeObj provided:', timeObj ? 'YES' : 'NO');
    
    try {
        let timeObject: any;

        if (timeObj) {
            timeObject = timeObj;
            console.log('✓ Using provided timeObj');
        } else {
            console.log('⏱️ Fetching current time details...');
            timeObject = await TimeService.getCurrentTimeDetails();
        }

        console.log('\n--- Time Object Details ---');
        console.log('timeObject:', JSON.stringify(timeObject, null, 2));

        if (timeObject) {
            let day = timeObject.day.toLowerCase();
            let minutesSinceMidnight = timeObject.minutesSinceMidnight;
            
            console.log('\n--- Extracted Time Info ---');
            console.log('Day of week:', day);
            console.log('Minutes since midnight:', minutesSinceMidnight);
            console.log('Time (human readable):', timeObject.currentTime);
            console.log(`\nChecking ${openingHoursArray.length} opening hour schedule(s)...`);

            // Check each opening hours schedule
            for (let i = 0; i < openingHoursArray.length; i++) {
                const rawOpeningHours = openingHoursArray[i];
                console.log(`\n━━━ Schedule ${i + 1}/${openingHoursArray.length} ━━━`);
                console.log('Raw schedule:', JSON.stringify(rawOpeningHours, null, 2));
                
                // Preprocess to handle overnight hours
                const openingHours = this.preprocessOvernightHours(rawOpeningHours);
                
                if (!openingHours[day] || (Array.isArray(openingHours[day]) && openingHours[day].length === 0)) {
                    console.log(`⚠️ No hours defined for "${day}" in this schedule (after preprocessing), skipping`);
                    continue;
                }

                console.log(`\n✓ Checking processed hours for "${day}":`, JSON.stringify(openingHours[day], null, 2));

                // Use isTimeInRange method
                const isOpen = this.isTimeInRange(openingHours[day], minutesSinceMidnight);
                
                if (isOpen) {
                    console.log('\n╔══════════════════════════════════════════════════════════╗');
                    console.log('║               ✅ STORE IS OPEN ✅                        ║');
                    console.log('╚══════════════════════════════════════════════════════════╝\n');
                    return true;
                }
            }
            
            console.log('\n╔══════════════════════════════════════════════════════════╗');
            console.log('║               ❌ STORE IS CLOSED ❌                      ║');
            console.log('╚══════════════════════════════════════════════════════════╝\n');
            return false;
        } else {
            console.log('⚠️ ITEM ACCEPTED BUT ERROR GETTING TIME');
            return true;
        }
    } catch (error) {
        console.log('❌ ERROR IN checkStoreIsOpen:', error);
        console.log('Returning TRUE (fail-safe behavior)');
        return true;
    }
}






}

export default TimeService;